import {
  EArrayItemType,
  IBuildConditionalRelationQueryParamsArgs,
  IBuildPatchBodyParams,
  IBuildPatchBodyResult,
  IContextSelectionMap,
  IContextSelectorDefinition,
  IFieldDefinition,
  IFormState,
  IShouldFetchConditionalRelationArgs,
  WorkflowNodeGroup,
} from "../interfaces";

export type Service = {
  id: string;
  name: string;
};

const INTERNAL_FORM_KEYS = ["__imageFile", "__imageFileName"];

const isEqualValue = (left: unknown, right: unknown): boolean => {
  return left === right;
};

export function arraySignature(ids: string[]) {
  return ids.join("|");
}

export function computeActiveTasksChange(original: any[], current: any[], enabled: boolean = true) {
  const originalIds = original.map((t) => t.id);
  const currentIds = current.map((t) => t.id);

  const originalSig = arraySignature(originalIds);
  const currentSig = arraySignature(currentIds);

  const originalSet = new Set(originalIds);
  const currentSet = new Set(currentIds);

  const addedIds = currentIds.filter((id) => !originalSet.has(id));
  const removedIds = originalIds.filter((id) => !currentSet.has(id));

  return {
    isDirty: originalSig !== currentSig && original.length > 1,
    addedIds,
    removedIds,
    originalIds,
    currentIds,
  };
}

export function getGroupLabel(group: WorkflowNodeGroup): string {
  switch (group) {
    case WorkflowNodeGroup.Catalogs:
      return "Catalogs";
    case WorkflowNodeGroup.Relationships:
      return "Relationships";
    default:
      return "Rules";
  }
}

export const buildInitialFormState = (fields: IFieldDefinition<any>[], source?: Record<string, unknown>): IFormState => {
  return fields.reduce<IFormState>((accumulator, field) => {
    const key = String(field.key);
    const sourceValue = source?.[key];

    if (field.type === "object") {
      accumulator[key] = buildInitialFormState(
        field.fields,
        sourceValue !== null && typeof sourceValue === "object" && !Array.isArray(sourceValue) ? (sourceValue as Record<string, unknown>) : undefined,
      );
      return accumulator;
    }

    if (field.type === "array") {
      accumulator[key] = formatArrayInputValue(sourceValue);
      return accumulator;
    }

    if (field.type === "number") {
      accumulator[key] = sourceValue ?? "";
      return accumulator;
    }

    if (field.type === "relation") {
      accumulator[key] = sourceValue ?? null;
      return accumulator;
    }

    accumulator[key] = sourceValue ?? "";
    return accumulator;
  }, {});
};

export const buildCatalogCreateBody = (values: IFormState, fields: IFieldDefinition<any>[]): Record<string, unknown> | FormData => {
  const normalizedValues = normalizeFormValuesByFields(values, fields);

  const { __imageFile, __imageFileName, imageUrl, ...rest } = normalizedValues;

  if (!__imageFile) {
    return rest;
  }

  const formData = new FormData();

  Object.entries(rest).forEach(([key, value]) => {
    appendFormDataValue(formData, key, value);
  });

  formData.append("image", __imageFile as File);

  return formData;
};

export const buildCatalogPatchBody = (params: IBuildPatchBodyParams): IBuildPatchBodyResult => {
  const { initialValues, currentValues } = params;

  const nextJsonPayload: Record<string, unknown> = {};
  let hasChanges = false;

  const imageFile = currentValues.__imageFile as File | undefined;
  const imageChanged = Boolean(imageFile);

  Object.entries(currentValues).forEach(([key, value]) => {
    if (INTERNAL_FORM_KEYS.includes(key)) {
      return;
    }

    if (key === "imageUrl") {
      if (!imageChanged) {
        return;
      }

      return;
    }

    const previousValue = initialValues[key];

    if (!isEqualValue(previousValue, value)) {
      nextJsonPayload[key] = value;
      hasChanges = true;
    }
  });

  if (imageChanged && imageFile) {
    const formData = new FormData();

    Object.entries(nextJsonPayload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (typeof value === "object") {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, String(value));
        }
      }
    });

    formData.append("image", imageFile);

    return {
      body: formData,
      isMultipart: true,
      hasChanges: true,
    };
  }

  return {
    body: nextJsonPayload,
    isMultipart: false,
    hasChanges,
  };
};

export const getValueByPath = (obj: Record<string, any>, path: string) => {
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
};

export const setValueByPath = (obj: Record<string, any>, path: string, value: any) => {
  const keys = path.split(".");
  const lastKey = keys[keys.length - 1];
  const result = { ...obj };

  let current: Record<string, any> = result;

  for (let index = 0; index < keys.length - 1; index++) {
    const key = keys[index];
    current[key] = {
      ...(current[key] ?? {}),
    };
    current = current[key];
  }

  current[lastKey] = value;

  return result;
};

export const appendFormDataValue = (formData: FormData, key: string, value: unknown): void => {
  if (value === undefined || value === null || value === "") {
    return;
  }

  if (value instanceof File) {
    formData.append(key, value);
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      appendFormDataValue(formData, `${key}[${index}]`, item);
    });
    return;
  }

  if (typeof value === "object") {
    Object.entries(value as Record<string, unknown>).forEach(([nestedKey, nestedValue]) => {
      appendFormDataValue(formData, `${key}[${nestedKey}]`, nestedValue);
    });
    return;
  }

  formData.append(key, String(value));
};

export const buildContextQueryParams = (selectors: IContextSelectorDefinition[] = [], values: Record<string, string>): Record<string, string> => {
  const queryParams: Record<string, string> = {};

  selectors.forEach((selector) => {
    const value = values[selector.key];

    if (!value) {
      return;
    }

    queryParams[selector.queryParamKey ?? selector.key] = value;
  });

  return queryParams;
};

export const getObjectValueByPath = (obj: unknown, path?: string): unknown => {
  if (!obj || !path) {
    return undefined;
  }

  return path.split(".").reduce<unknown>((acc, key) => {
    if (acc && typeof acc === "object" && key in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[key];
    }

    return undefined;
  }, obj);
};

const hasCompleteContextSelection = (items?: IContextSelectorDefinition[], values?: IContextSelectionMap): boolean => {
  if (!items || items.length === 0 || !values) {
    return true;
  }

  return items.every((selector) => Boolean(values[selector.key]));
};

export const shouldFetchConditionalRelation = (args: IShouldFetchConditionalRelationArgs): boolean => {
  const { selectors, contextSelection, config } = args;

  const requireAllSelectorsBeforeFetch = config?.requireAllSelectorsBeforeFetch ?? false;
  const fetchOnMount = config?.fetchOnMount ?? true;

  if (!selectors?.length) {
    return fetchOnMount;
  }

  if (!requireAllSelectorsBeforeFetch) {
    return fetchOnMount;
  }

  return hasCompleteContextSelection(selectors, contextSelection);
};

export const buildConditionalRelationQueryParams = (args: IBuildConditionalRelationQueryParamsArgs): Record<string, string | number> => {
  const { selectors, contextSelection, page, pageSize } = args;

  const params: Record<string, string | number> = {
    page,
    pageSize,
  };

  if (!selectors?.length) {
    return params;
  }

  selectors.forEach((selector) => {
    const selectedValue = contextSelection[selector.key];

    if (!selectedValue) {
      return;
    }

    const paramKey = selector.queryParamKey ?? selector.key;
    params[paramKey] = selectedValue;
  });

  return params;
};

export const parseArrayInputValue = (value: string, itemType: EArrayItemType = EArrayItemType.STRING): string[] | number[] => {
  const items = value
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item !== "");

  if (itemType === EArrayItemType.NUMBER) {
    return items.map((item) => Number(item)).filter((item) => !Number.isNaN(item));
  }

  return items;
};

export const formatArrayInputValue = (value: unknown): string => {
  if (!Array.isArray(value)) {
    return "";
  }

  return value.join(", ");
};

export const normalizeFormValuesByFields = (values: IFormState, fields: IFieldDefinition<any>[]): IFormState => {
  const normalized = fields.reduce<IFormState>((accumulator, field) => {
    const key = String(field.key);
    const currentValue = values[key];

    if (field.type === "object") {
      accumulator[key] = normalizeFormValuesByFields((currentValue as IFormState) ?? {}, field.fields);
      return accumulator;
    }

    if (field.type === "array") {
      accumulator[key] = parseArrayInputValue(typeof currentValue === "string" ? currentValue : "", field.itemType);
      return accumulator;
    }

    accumulator[key] = currentValue;
    return accumulator;
  }, {});

  if ("__imageFile" in values) {
    normalized.__imageFile = values.__imageFile;
  }

  if ("__imageFileName" in values) {
    normalized.__imageFileName = values.__imageFileName;
  }

  if ("imageUrl" in values) {
    normalized.imageUrl = values.imageUrl;
  }

  return normalized;
};
